/**
 * Handles Measurement utilities.
 *
 * @param millimeter
 */
export default class MeasurementUtility {
	/**
	 * Metric measurements to imperial measurements.
	 *
	 * @param millimeter Number to convert.
	 * @param inFeet If it should contain feet.
	 * @param withoutMeasureUnit Return with measurement unit or not.
	 * @returns Numbers in imperial.
	 */
	public static metricToImperial(
		millimeter: number,
		inFeet = false,
		withoutMeasureUnit = false
	): string {
		const inInch = this.roundToEighths(this.millimeterToInch(millimeter));
		let feet = 0;
		let inches = '0';

		if (inFeet) {
			feet = (inInch / 12) << 0;
			inches = this.decimalsToFractions(inInch % 12);
		} else {
			inches = this.decimalsToFractions(inInch);
		}

		if (feet !== 0 && inches !== '0') {
			return `${feet} ft ${inches} in`;
		} else if (feet !== 0) {
			return `${feet} ft`;
		}

		return withoutMeasureUnit ? inches : `${inches} in`;
	}

	/**
	 * Millimeter to inch.
	 *
	 * @param millimeter Number to convert.
	 * @returns Number in Inches.
	 */
	private static millimeterToInch(millimeter: number): number {
		return millimeter * 0.0393700787;
	}

	/**
	 * Round a number to closest eighths.
	 *
	 * @param inch Number to convert.
	 * @returns Number in Inches rounded to closest eights.
	 */
	private static roundToEighths(inch: number): number {
		return parseFloat((Math.round(inch * 8) / 8).toFixed(3));
	}

	/**
	 * Greatest common divisor.
	 *
	 * @param a Number.
	 * @param b Number.
	 * @returns Number the greatest common divisor.
	 */
	private static greatestCommonDivisor(a: number, b: number): number {
		if (b === 0) {
			return a;
		}
		return this.greatestCommonDivisor(b, a % b);
	}

	/**
	 * Decimals to fractions.
	 *
	 * @param decimals Number e.g 1.125.
	 * @returns String the greatest common divisor.
	 */
	private static decimalsToFractions(decimals: number): string {
		const decimalArray = decimals.toString().split('.');

		if (decimalArray.length === 1) {
			return decimalArray[0];
		}

		const leftDecimalPart = parseInt(decimalArray[0]);
		const rightDecimalPart = decimalArray[1];

		let numerator = parseInt(rightDecimalPart);
		let denominator = 10 ** rightDecimalPart.length;
		const factor = this.greatestCommonDivisor(numerator, denominator);

		numerator /= factor;
		denominator /= factor;

		return leftDecimalPart !== 0
			? `${leftDecimalPart} ${numerator}/${denominator}`
			: `${numerator}/${denominator}`;
	}
}
